/**
 * Lista «Acessos da equipe da Pratzu» (#430) — Card no fim de Usuários.
 * Só dono / platform_owner; nunca IP, UA nem ator.
 */
import { useCallback, useEffect, useState } from 'react'
import { Button, Card, Empty, Spinner, toast } from '../ui/index.js'
import { platformAccess, config, type PlatformAccessItem } from '../../lib/api.js'
import { formatInRestaurant } from '@self-restaurant/shared/src/utils/timezone'
import { intlLocaleFor } from '@self-restaurant/shared'
import { useI18n } from '../../lib/i18n.js'
import { getUserRoles } from '../../lib/auth.js'
import { formatDurationMinutes } from '../../lib/impersonation-a11y.js'

function canViewAccessList(): boolean {
  const roles = getUserRoles()
  return roles.includes('restaurant_owner') || roles.includes('platform_owner')
}

export function durationLabel(
  item: PlatformAccessItem,
  t: (k: string, params?: Record<string, string>) => string,
  nowSec: number,
  locale: string,
): string {
  if (item.ended_at === null && item.expires_at > nowSec) {
    return t('admin.impersonation.access_active')
  }
  if (item.ended_by === 'expired') {
    const mins = Math.max(1, Math.round((item.expires_at - item.started_at) / 60))
    return t('admin.impersonation.access_expired', {
      duration: formatDurationMinutes(mins, locale),
    })
  }
  const end = item.ended_at ?? item.expires_at
  const mins = Math.max(1, Math.round((end - item.started_at) / 60))
  return formatDurationMinutes(mins, locale)
}

export function PlatformAccessCard() {
  const { t, lang } = useI18n()
  const [items, setItems] = useState<PlatformAccessItem[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(false)
  const [timezone, setTimezone] = useState<string | null>(null)
  const [countryCode, setCountryCode] = useState<string | null>(null)

  const visible = canViewAccessList()
  const intlLocale = intlLocaleFor(lang, countryCode)

  const load = useCallback(
    async (next?: string | null, append = false) => {
      if (append) setLoadingMore(true)
      else {
        setLoading(true)
        setError(false)
      }
      try {
        const data = await platformAccess.list({
          ...(next ? { cursor: next } : {}),
          limit: 20,
        })
        setItems((prev) => (append ? [...prev, ...data.items] : data.items))
        setCursor(data.next_cursor)
      } catch {
        if (append) {
          toast.error(t('admin.impersonation.access_err'))
        } else {
          setError(true)
          setItems([])
        }
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [t],
  )

  useEffect(() => {
    if (!visible) return
    void load()
    void config
      .get()
      .then((c) => {
        setTimezone(c.timezone ?? null)
        setCountryCode(c.country_code ?? null)
      })
      .catch(() => {
        setTimezone(null)
        setCountryCode(null)
      })
  }, [visible, load])

  if (!visible) return null

  const nowSec = Math.floor(Date.now() / 1000)

  return (
    <div data-testid="platform-access-card" className="mt-6">
      <Card title={t('admin.impersonation.access_title')}>
        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner label={t('admin.common.loading')} />
          </div>
        ) : error ? (
          <div className="text-center py-6 space-y-3" data-testid="platform-access-error">
            <p className="text-sm text-gray-600">{t('admin.impersonation.access_err')}</p>
            <Button
              variant="secondary"
              data-testid="platform-access-retry"
              onClick={() => void load()}
            >
              {t('admin.impersonation.access_retry')}
            </Button>
          </div>
        ) : items.length === 0 ? (
          <Empty title={t('admin.impersonation.access_empty')} />
        ) : (
          <div>
            <div
              className="hidden sm:grid sm:grid-cols-[minmax(0,1.2fr)_auto_auto_minmax(0,1.5fr)] sm:gap-3 pb-2 text-xs font-semibold text-gray-500 text-start"
              role="row"
            >
              <span>{t('admin.impersonation.access_col_when')}</span>
              <span>{t('admin.impersonation.access_col_duration')}</span>
              <span>{t('admin.impersonation.access_col_who')}</span>
              <span>{t('admin.impersonation.access_col_reason')}</span>
            </div>
            <ul className="divide-y divide-gray-100">
              {items.map((item) => {
                const when = formatInRestaurant(item.started_at * 1000, intlLocale, timezone, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })
                return (
                  <li
                    key={item.session_id}
                    data-testid={`platform-access-row-${item.session_id}`}
                    className="py-3 flex flex-col gap-1 sm:grid sm:grid-cols-[minmax(0,1.2fr)_auto_auto_minmax(0,1.5fr)] sm:gap-3 sm:items-start"
                  >
                    <span className="text-sm text-gray-800 text-start">
                      <span className="sm:hidden text-xs font-semibold text-gray-500 me-2">
                        {t('admin.impersonation.access_col_when')}:{' '}
                      </span>
                      {when}
                    </span>
                    <span className="text-sm text-gray-600">
                      <span className="sm:hidden text-xs font-semibold text-gray-500 me-2">
                        {t('admin.impersonation.access_col_duration')}:{' '}
                      </span>
                      {durationLabel(item, t, nowSec, intlLocale)}
                    </span>
                    <span className="text-sm text-gray-600">
                      <span className="sm:hidden text-xs font-semibold text-gray-500 me-2">
                        {t('admin.impersonation.access_col_who')}:{' '}
                      </span>
                      {t('admin.impersonation.access_who')}
                    </span>
                    <span className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                      <span className="sm:hidden text-xs font-semibold text-gray-500 me-2">
                        {t('admin.impersonation.access_col_reason')}:{' '}
                      </span>
                      {item.reason}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
        {!loading && !error && cursor && (
          <div className="pt-4 flex justify-center">
            <Button
              variant="secondary"
              loading={loadingMore}
              data-testid="platform-access-more"
              onClick={() => void load(cursor, true)}
            >
              {t('admin.impersonation.access_more')}
            </Button>
          </div>
        )}
      </Card>
    </div>
  )
}
